<template>
    <b-modal
        v-model="showModal"
        size="md"
        centered
        :title="$t('MassImportCertificates')"
        body-class="p-4"
        no-close-on-backdrop>
        
        <div class="w-100">
            <div class="mb-4 p-3 rounded border">
                <h6 class="font-weight-semibold">{{$t('Step') + ' 1'}}</h6>
                <p class="text-muted small mb-2">
                    {{ $t('DownloadExcelModel') }}
                </p>
                <b-button :variant="'outline-' + Variant" size="sm" @click="downloadTemplate" :disabled="isDownloading">
                    <b-spinner small v-if="isDownloading" class="mr-2"></b-spinner>
                    <i class="fas fa-download mr-1" v-else></i> {{$t('DownloadModel')}}
                </b-button>
            </div>
            
            <div class="mb-4 p-3 rounded border">
                <h6 class="font-weight-semibold">{{$t('Step') + ' 2'}}</h6>
                <b-form-group label-for="file-upload">
                    <b-form-file
                        id="file-upload"
                        v-model="selectedFile"
                        :state="Boolean(selectedFile)"
                        :placeholder="$t('PutYourFileHere')" 
                        :drop-placeholder="$t('DropFileHere')"
                        accept=".xlsx"
                        browse-text="Parcourir">
                    </b-form-file>
                </b-form-group>
            </div>

            <b-alert 
                variant="danger" 
                :show="importErrors.length > 0" 
                class="mt-3 mb-0" 
                style="max-height: 200px; overflow-y: auto;">
                <h6 class="alert-heading font-weight-semibold">Import impossible :</h6>
                <ul class="mb-0 pl-3 small">
                    <li v-for="(error, index) in importErrors" :key="index">{{ error }}</li>
                </ul>
            </b-alert>
        </div>
        
        <template #modal-footer>
            <div class="w-100 d-flex justify-content-end">
                <b-button 
                    :variant="'outline-' + Variant" 
                    class="mr-2"
                    pill
                    @click="closeModal"
                    :disabled="isUploading">
                    {{$t('Cancel')}}
                </b-button>
                <b-button 
                    :variant="Variant" 
                    @click="onSubmit" 
                    pill
                    :disabled="!selectedFile || isUploading">
                    <b-spinner small v-if="isUploading" class="mr-2"></b-spinner>
                    {{$t('Import')}}
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import locale from '../locales/SMA.json';
import { mixins } from 'vue-class-component';
import { SMAUtils } from './SMAUtils';

@Component({
    i18n : { messages: locale }
})
/**
 * Composant pour l'importation / création en masse de mandat distributeur
 */
export default class CertificatesImport extends mixins (Vue, SMAUtils) {
    @PropSync('Show', { type: Boolean, required: true })
    private showModal!: boolean;

    @Prop({ type: String, default: 'primary' }) 
    public Variant!: string;

    private selectedFile: File | null = null;
    private isUploading: boolean = false;
    private isDownloading: boolean = false;
    private importErrors: string[] = [];

    /**
     * Watcher sur l'ajout d'un fichier d'import
     */
    @Watch('selectedFile')
    private onFileChanged() {
        this.importErrors = [];
    }

    /**
     * Fermeture de la modal et reset des propriétés composant
     */
    private closeModal(): void {
        this.showModal = false;
        this.selectedFile = null;
        this.isUploading = false;
        this.importErrors = [];
    }

    /**
     * Téléchargement du template pour la création en masse de mandats
     */
    private async downloadTemplate(): Promise<void> {
        this.isDownloading = true;
        try {
            const result = await this.$store.dispatch("CertificateStore/DownloadMassImportTemplate");
            if (!result.success) {
                console.error(result.message);
                this.$bvToast.toast(result.message, { title: 'Erreur', variant: 'danger' });
            }
        } finally {
            this.isDownloading = false;
        }
    }

    /**
     * Validation d'import et envoi du fichier excel coté serveur
     */
    private async onSubmit(): Promise<void> {
        if (!this.selectedFile) return;

        this.isUploading = true;
        this.importErrors = [];

        try {
            const result = await this.$store.dispatch("CertificateStore/UploadMassImport", { 
                file: this.selectedFile 
            });

            if (result.success) {
                this.$emit("ImportSuccess", result.data);
                this.showToast('Succès', 'success', result.message);
                this.closeModal();
            } else {
                if (result.data && result.data.Errors && result.data.Errors.length > 0) {
                    this.importErrors = result.data.Errors;
                } else {
                    this.showToast('Erreur', 'danger', result.message);
                }
            }
        } finally {
            this.isUploading = false;
        }
    }
}
</script>